import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import bgImage from '../images/background.png';

function OwIcon({ name, size = 20 }) {
    const p = { width: size, height: size, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
    switch (name) {
        case 'home':  return <svg {...p}><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
        case 'back':  return <svg {...p}><path d="M19 12H5M12 19l-7-7 7-7"/></svg>;
        case 'trash': return <svg {...p}><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>;
        case 'send':  return <svg {...p}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>;
        default: return null;
    }
}

const RANK_LABELS = ['1°', '2°', '3°', '4°'];

export default function OrderWine() {
    const { t } = useTranslation();
    const [availableWines, setAvailableWines] = useState(['A', 'B', 'C', 'D']);
    const [selectedWines, setSelectedWines] = useState([]);
    const [popupVisible, setPopupVisible] = useState(false);
    const [ready, setReady] = useState(false);
    const [saving, setSaving] = useState(false);
    const [submitError, setSubmitError] = useState(false);
    const navigate = useNavigate();

    useEffect(() => { requestAnimationFrame(() => setReady(true)); }, []);

    const handleWineClick = (wine) => {
        setAvailableWines(prev => prev.filter(w => w !== wine));
        setSelectedWines(prev => [...prev, wine]);
    };

    const handleClearSelected = () => {
        setAvailableWines(prev => [...prev, ...selectedWines]);
        setSelectedWines([]);
    };

    const handleSaveOrder = () => {
        const userLogged = JSON.parse(localStorage.getItem('userLogged'));
        if (!userLogged) return;
        if (selectedWines.length !== 4) return;

        const order = {
            user_id: userLogged.id,
            first: selectedWines[0] || '',
            second: selectedWines[1] || '',
            third: selectedWines[2] || '',
            fourth: selectedWines[3] || '',
        };

        setSaving(true);
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${process.env.REACT_APP_API_URL}/storeWinesOrder`, true);
        xhr.setRequestHeader("Content-Type", "application/json;charset=UTF-8");
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4) {
                setSaving(false);
                if (xhr.status === 200) {
                    localStorage.removeItem('selectedWines');
                    navigate('/');
                } else {
                    setSubmitError(true);
                }
            }
        };
        xhr.send(JSON.stringify(order));
    };

    return (
        <div className={`ow-stage${ready ? ' ow-ready' : ''}`}>
            <div className="ow-bg" style={{ backgroundImage: `url(${bgImage})` }} />
            <div className="ow-vignette" />
            <div className="ow-grain" />

            <div className="ow-topbar">
                <button className="ow-icon-btn" onClick={() => navigate('/')} aria-label={t('common.home')}>
                    <OwIcon name="home" size={16} />
                </button>
                <span className="ow-topbar-title">{t('order_wine.title')}</span>
                <button className="ow-icon-btn" onClick={() => navigate(-1)} aria-label={t('common.back')}>
                    <OwIcon name="back" size={16} />
                </button>
            </div>

            <div className="ow-body">
                <h2 className="ow-heading">{t('order_wine.title')}</h2>

                <div className="ow-cols">
                    {/* Left column: available wines */}
                    <div className="ow-col">
                        <div className="ow-col-title">{t('order_wine.wines_tasted')}</div>
                        {availableWines.map(wine => (
                            <button
                                key={wine}
                                className="ow-wine-btn"
                                onClick={() => handleWineClick(wine)}
                            >
                                {wine}
                            </button>
                        ))}
                        {availableWines.length === 0 && (
                            <div style={{ fontSize: 11, opacity: .4, textAlign: 'center', marginTop: 12 }}>—</div>
                        )}
                    </div>

                    {/* Right column: selected ranking */}
                    <div className="ow-col">
                        <div className="ow-col-title">{t('order_wine.best_wine')}</div>
                        <div className="ow-ranks">
                            {RANK_LABELS.map((rank, i) => (
                                <div
                                    key={rank}
                                    className={`ow-slot${selectedWines[i] ? '' : ' ow-slot-empty'}`}
                                >
                                    <span className="ow-slot-num">{rank}</span>
                                    <span className="ow-slot-label">
                                        {selectedWines[i] || '—'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <div className="ow-bottom">
                {selectedWines.length > 0 && (
                    <button className="ow-clear-btn" onClick={handleClearSelected} aria-label={t('common.clear')}>
                        <OwIcon name="trash" size={14} />
                    </button>
                )}
                <button
                    className="ow-submit-btn"
                    onClick={() => setPopupVisible(true)}
                    disabled={selectedWines.length !== 4}
                >
                    {t('common.send')}
                </button>
            </div>

            {popupVisible && (
                <div className="ow-popup-overlay">
                    <div className="ow-popup">
                        <p className="ow-popup-text">{submitError ? t('common.fetch_error') : t('order_wine.confirm_submit')}</p>
                        <div className="ow-popup-actions">
                                            <button className="ow-popup-cancel" onClick={() => setPopupVisible(false)} disabled={saving}>
                                {t('common.close')}
                            </button>
                            <button className="ow-popup-confirm" onClick={handleSaveOrder} disabled={saving}>
                                {saving ? <span className="ow-spinner" /> : t('common.send')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
